import {
  ANCHOR_TYPE,
  AVAILABILITY_MODE,
  END_RULES_COMBINE,
  END_RULE_TYPE,
  REPEAT_MODE,
} from '../constants'
import { selectedInstanceIds } from '../hooks/anchorConstraints'
import { namesForAnchor } from './entityNames'
import { summarizeAnchorShort, summarizeCombinedShort } from './anchorSummary'
import { buildScheduleLabel } from './plainLanguage'
import { summarizeRepeat } from './repeatSummary'

const formatOpenFor = availability => {
  const value = availability?.openFor?.value
  const unit = availability?.openFor?.unit || 'hours'
  if (availability?.mode !== AVAILABILITY_MODE.fixedDuration || !value) return ''
  return `Open ${value} ${unit}`
}

const shortRepeat = schedule => {
  const mode = schedule?.repeat?.mode
  if (!mode || mode === REPEAT_MODE.doesNotRepeat) {
    const visitCount = (schedule?.anchors || []).reduce((count, anchor) => {
      if (anchor.type !== ANCHOR_TYPE.visit) return count
      return Math.max(count, selectedInstanceIds(anchor).length)
    }, 0)
    if (visitCount > 1) return `${visitCount} instances · doesn't repeat`
    return "doesn't repeat"
  }
  if (mode === REPEAT_MODE.oncePerAnchorOccurrence) {
    const cycleAnchored = (schedule?.anchors || []).some(anchor => anchor.type === ANCHOR_TYPE.visitCycle)
    return cycleAnchored ? 'one per cycle' : 'once per occurrence'
  }
  return summarizeRepeat(schedule.repeat)
}

const shortEnd = (endRules, entityOptions) => {
  const rules = endRules?.rules || []
  if (!rules.length) return ''
  const bits = rules.map(rule => {
    if (rule.type === END_RULE_TYPE.afterNOccurrences) return `${rule.count}×`
    if (rule.type === END_RULE_TYPE.relativeToVisit) {
      const amount = rule.offset?.amount ?? rule.offset?.value ?? 0
      const unit = (rule.offset?.unit || 'days')[0]
      const entry = Object.values(rule.byArm || {}).find(Boolean)
      const firstVisitId = entry?.visitId || entry?.visit_id
      const visit = (entityOptions?.visits || []).find(item => String(item.id) === String(firstVisitId))
      const visitName = visit?.name || ''
      return visitName ? `${visitName} +${amount} ${unit}` : `+${amount} ${unit} after visit`
    }
    return ''
  }).filter(Boolean)
  if (!bits.length) return ''
  const prefix = endRules.combine === END_RULES_COMBINE.latestOf ? 'latest of ' : ''
  return `ends ${prefix}${bits.join(' or ')}`
}

const participantStartHint = (schedule, headline) => {
  const onlyStart =
    !schedule.combineAnchors &&
    schedule.anchors?.[0]?.type === ANCHOR_TYPE.participantStartDate
  if (!onlyStart) return headline
  return `${headline} · not tied to any visit`
}

export const buildScheduleCard = (schedule, index, selected, entityOptions = {}) => {
  const label = schedule?.label || schedule?.name || buildScheduleLabel(schedule)
  const nameFor = anchor => namesForAnchor(anchor, entityOptions)
  const headline = schedule?.combineAnchors
    ? summarizeCombinedShort(schedule, nameFor)
    : summarizeAnchorShort(schedule?.anchors?.[0], nameFor(schedule?.anchors?.[0]))
  const metaBits = [
    formatOpenFor(schedule?.availability),
    shortRepeat(schedule),
    shortEnd(schedule?.endRules, entityOptions),
  ].filter(Boolean)

  return {
    kicker: `Schedule ${index + 1}`,
    kind: selected ? `Editing · ${label}` : label,
    headline: participantStartHint(schedule, headline),
    meta: metaBits.join(' · '),
  }
}
